import LeaseTypeSelector from "./LeaseTypeSelector";
import { fmtCurrency, fmtDate, fmtDateShort, fmtPSF, fmtSF } from "../utils/format";

// ---------------------------------------------------------------------------
// Rent Roll Parse Result
// ---------------------------------------------------------------------------

interface RentRollResultProps {
  result: Record<string, unknown>;
  dealId: string;
}

export function RentRollResultView({ result, dealId }: RentRollResultProps) {
  const rawTenants = (result.tenants as Array<Record<string, unknown>>) ?? [];
  const tenants = rawTenants
    .map((t) => (t.tenant as Record<string, unknown>) ?? t)
    .filter((td) => td && td.tenant_name)
    .sort((a, b) => {
      const aNum = parseInt(((a.suite_id as string) || "").match(/^\d+/)?.[0] || "9999", 10);
      const bNum = parseInt(((b.suite_id as string) || "").match(/^\d+/)?.[0] || "9999", 10);
      return aNum - bNum;
    });

  const allGross = tenants.length > 0 && tenants.every((td) => {
    const lt = ((td.lease_type as string) || "").toLowerCase();
    if (lt !== "gross") return false;
    const cam = td.cam_structure as Record<string, unknown> | undefined;
    if (!cam) return true;
    return !cam.tax_reimbursement && !cam.insurance_reimbursement;
  });
  const effectiveLeaseType = (td: Record<string, unknown>): string => {
    const lt = (td.lease_type as string) || "";
    if (allGross && lt.toLowerCase() === "gross") return "pending";
    return lt;
  };
  const allExtras = (result.vacant_units as Array<Record<string, unknown>>) ?? [];
  const vacantUnits = allExtras.filter((v) => {
    const name = ((v.tenant_name as string) || "").toLowerCase();
    const sf = (v.gla_sf as number) || (v.leased_sf as number) || 0;
    const isVacant = v.is_vacant === true || !name || name.includes("vacant") || name.includes("available");
    return isVacant && !(sf === 0 && name);
  });
  const miscRevenue = allExtras.filter((v) => !vacantUnits.includes(v));
  const confidenceScore = result.confidence_score as number | undefined;

  return (
    <div className="pt-4 sm:pt-5 space-y-4 sm:space-y-5">
      {confidenceScore != null && (
        <div className="space-y-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.06em] text-bark-400">
              Parse Confidence
            </span>
            <div className="flex-1 max-w-xs">
              <div className="h-2 sm:h-2.5 bg-grove-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    confidenceScore >= 85 ? "bg-forest-500"
                    : confidenceScore >= 60 ? "bg-honey-500"
                    : confidenceScore >= 35 ? "bg-orange-500"
                    : "bg-red-500"
                  }`}
                  style={{ width: `${confidenceScore}%` }}
                />
              </div>
            </div>
            <span className="text-xs sm:text-sm font-semibold text-bark-700">{confidenceScore}/100</span>
          </div>
          <p className="text-xs text-bark-400 leading-relaxed hidden sm:block">
            Parse confidence measures how reliably the system extracted structured data from your document.
            A high score means tenant names, square footages, rent amounts, and lease dates were clearly
            identified. A lower score may indicate ambiguous formatting, missing fields, or unusual
            document layout — review the parsed data before relying on it for underwriting.
          </p>
        </div>
      )}
      {/* Flags are rendered by ValidationFlagsSection in DocumentsPage */}
      {tenants.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Parsed Tenants ({tenants.length})</h4>
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Suite</th><th>Tenant</th><th className="num">SF</th><th className="num">Rent PSF</th><th>Lease Type</th><th>Expiration</th></tr></thead>
              <tbody>
                {tenants.slice(0, 30).map((td, i) => (
                  <tr key={i}>
                    <td className="text-bark-500">{((td.suite_id as string) || "\u2014").replace(/\band\b/gi, "&")}</td>
                    <td className="font-medium text-bark-700">{td.tenant_name as string}</td>
                    <td className="num">{fmtSF((td.leased_sf as number) ?? 0)}</td>
                    <td className="num">{fmtPSF((td.base_rent_annual_psf as number) ?? 0)}</td>
                    <td><LeaseTypeSelector type={effectiveLeaseType(td)} tenantId={String(td.tenant_id || i)} dealId={dealId || ""} readOnly /></td>
                    <td>{td.lease_expiration_date ? fmtDate(td.lease_expiration_date as string) : "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Vacancy section */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
          Available Suites {vacantUnits.length > 0 ? `(${vacantUnits.length})` : ""}
        </h4>
        {vacantUnits.length > 0 ? (
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Suite</th><th>Space</th><th className="num">SF</th><th>Status</th><th>Notes</th></tr></thead>
              <tbody>
                {vacantUnits.map((v, i) => (
                  <tr key={i} className="bg-honey-50">
                    <td className="text-bark-500">{(v.unit as string) || (v.suite_id as string) || "\u2014"}</td>
                    <td className="font-medium text-honey-800">{(v.tenant_name as string) || "Available"}</td>
                    <td className="num">{fmtSF((v.gla_sf as number) || (v.leased_sf as number) || 0)}</td>
                    <td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-honey-100 text-honey-800 border border-honey-200">
                        {(v.lease_status as string) || "Vacant"}
                      </span>
                    </td>
                    <td className="text-bark-400">
                      {v.begin_date && v.end_date
                        ? `${fmtDateShort(v.begin_date as string)} \u2013 ${fmtDateShort(v.end_date as string)}`
                        : "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Suite</th><th>Space</th><th className="num">SF</th><th>Status</th><th>Notes</th></tr></thead>
              <tbody>
                <tr>
                  <td colSpan={5} className="text-center text-bark-400 py-6">
                    No available suites — property is fully leased
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Miscellaneous revenue */}
      {miscRevenue.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">Miscellaneous Revenue ({miscRevenue.length})</h4>
          <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
            <table className="w-full data-table text-[12px]">
              <thead><tr><th>Location</th><th>Description</th><th className="num">Annual Revenue</th><th>Term</th></tr></thead>
              <tbody>
                {miscRevenue.map((v, i) => {
                  const fin = (v.financials as Record<string, Record<string, number>>) || {};
                  const annRent = fin.rent?.ann || 0;
                  return (
                    <tr key={i}>
                      <td className="text-bark-500">{(v.unit as string) || (v.suite_id as string) || "\u2014"}</td>
                      <td className="font-medium text-bark-700">{(v.tenant_name as string) || "Other"}</td>
                      <td className="num">{annRent > 0 ? fmtCurrency(annRent) : "\u2014"}</td>
                      <td className="text-bark-400">
                        {v.begin_date && v.end_date
                          ? `${fmtDateShort(v.begin_date as string)} \u2013 ${fmtDateShort(v.end_date as string)}`
                          : "\u2014"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
